import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X } from 'lucide-react';
import './DocumentUpload.css';

const DocumentUpload = ({ onDocumentUpload }) => {
  const [formData, setFormData] = useState({
    documentName: '',
    category: '',
    location: 'inbound'
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      // Auto-populate document name with file name (without extension)
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      setFormData(prev => ({
        ...prev,
        documentName: fileName
      }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedFile || !formData.documentName || !formData.category) {
      alert('Please fill in all required fields and select a file.');
      return;
    }

    // Call the parent handler with document info
    onDocumentUpload({
      ...formData,
      file: selectedFile,
      name: formData.documentName
    });

    // Reset form
    setFormData({
      documentName: '',
      category: '',
      location: 'inbound'
    });
    setSelectedFile(null);
  };

  return (
    <div className="document-upload">
      <div className="upload-header">
        <Upload size={24} />
        <h2>Manual Invoice Upload</h2>
      </div>

      <form onSubmit={handleSubmit} className="upload-form">
        <div className="form-group">
          <label htmlFor="documentName">
            Document Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="documentName"
            name="documentName"
            value={formData.documentName}
            onChange={handleInputChange}
            placeholder="Upload a document to set name"
            required
            disabled={!selectedFile}
            className={!selectedFile ? 'disabled-input' : ''}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">
            Document Category <span className="required">*</span>
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            required
          >
            <option value="">Select category</option>
            <option value="invoice">Invoice</option>
            <option value="contract">Contract</option>
            <option value="report">Report</option>
            <option value="legal">Legal Document</option>
            <option value="financial">Financial Document</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Upload Document <span className="required">*</span>
          </label>
          
          <div 
            {...getRootProps()} 
            className={`dropzone ${isDragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
          >
            <input {...getInputProps()} />
            
            {selectedFile ? (
              <div className="file-preview">
                <div className="file-info">
                  <FileText size={32} />
                  <div className="file-details">
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="remove-file"
                  onClick={handleRemoveFile}
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div className="dropzone-content">
                <Upload size={48} />
                <p className="dropzone-text">
                  {isDragActive 
                    ? "Drop the file here..." 
                    : "Click to upload or drag and drop"
                  }
                </p>
                {/* <p className="dropzone-subtext">
                  PDF, DOC, DOCX, TXT, XLS, XLSX, CSV (Max 10MB)
                </p> */}
              </div>
            )}
          </div>
        </div>

        <button type="submit" className="upload-btn" disabled={!selectedFile}>
          Upload Document
        </button>
      </form>
    </div>
  );
};

export default DocumentUpload; 